/*!
 * doTERRA Web-UI
 * http://www.doterra.com
 *
 * Copyright 2019 doTERRA Holdings, LLC
 * All Rights Reserved
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Button, Icon} from './controls';
import {usePaymentsMutator, usePaymentsState} from '../contexts/payments';
import {
  applePaySupported,
  formatCCExpiry,
  getCCInfo,
  getIconNameForPaymentType,
  getNameForPaymentType,
  initiateApplePay,
  initiateGooglePay,
  initiatePayPal
} from '../utils/payments';
import {text} from '../utils/i18n';
import {PaymentView, usePaymentViewMutator, usePaymentViewState} from "../containers/payment-view";
import {TitledPanel} from "./panels";
import {ErrorModal, Modal, SuccessModal} from './modals';
import {Tooltip} from 'react-tooltip';
import {Checkbox} from "./fields";
import {post} from "../utils/rmi";
import {applePay, client} from "braintree-web";
import {useCustomerState} from "../contexts/customer";
import {useModalsMutator, useSiteState} from "../contexts/page";
import {useMarketState} from "../contexts/market";
import {convertRegionToIsocode} from "../data";
import {useFormObserver} from '../contexts/form-observer';
import {flushSync} from "react-dom";

/*********************************/
/** Credit Card Payment         **/
/*********************************/
const CreditCardPayment = (props) => {
  const { payment, editClickHandler, titledPanel, clickHandler, isCartPayment } = props;
  const isExpired = payment && payment.status === 'EXPIRED';
  const {isSubscriptionsPage, context} = usePaymentsState();

  let paymentPanelStyling = {};
  if (!titledPanel) {
    paymentPanelStyling = {...paymentPanelStyling, height: '140px', padding: '20px'};
  }
  if (isCartPayment) {
    paymentPanelStyling = {...paymentPanelStyling, backgroundColor: 'rgba(189, 139, 202, .25)', border: '3px solid #BD8BCA'};
  }
  const defaultStyling = {
    color: '#BD8BCA',
    paddingTop: '8px',
    fontStyle: 'italic'
  };
  const paymentTypeStyling = {
    position: 'relative',
    paddingLeft: '60px',
    marginBottom: '10px'
  };
  const paymentIconStyling = {
    fontSize: '18pt',
    position: 'absolute',
    top: '-12px',
    left: '0px'
  };
  const ccNumberStyling = {
    paddingRight: '40px',
    fontWeight: '700'
  };
  const paymentLineStyling = {
    display: 'flex',
    paddingTop: '8px'
  };
  let editIconStyling = {};
  if (!titledPanel) {
    editIconStyling = {...editIconStyling, cursor: 'pointer', position: 'absolute', right: '45px', top: '20px'};
  }
  const ccIdStyling = {
    flex: '1 0 auto'
  };
  const ccExpiryStyling = {
    flex: '1',
    paddingTop: '24px'
  };
  const expiredStyling = {
    paddingLeft: '40px',
    color: 'red',
    fontSize: '14px',
    fontStyle: 'italic'
  };
  let titledPanelStyling ={
    padding: '0'
  };
  if (!titledPanel) {
    titledPanelStyling = {};
  }

  const editText = text("text.button.label.edit");

  return (
    <div className={titledPanel ? "card__content payment__content" : "panel panel-default"} onClick={()=>clickHandler(context)} style={paymentPanelStyling}>
      <div className="payment-type" style={paymentTypeStyling}>
        <Icon name={getIconNameForPaymentType(payment.type)} cssStyles={paymentIconStyling} />
        <span data-testid="ccard-name">{payment.name}</span>
      </div>
      <div>
        <span data-testid="ccard-num-masked" style={ccNumberStyling}>{payment.number}</span>
        <span data-testid="ccard-exp">{formatCCExpiry(payment.expiryMonth, payment.expiryYear)}</span>
        {isExpired && <span data-testid="ccard-num-expired" style={expiredStyling}>Expired</span>}
      </div>
      {payment.defaultPayment && !isSubscriptionsPage ? <div style={defaultStyling}>{text("form.paymentManagement.default")}</div> : null}
      {editClickHandler &&
        <div className="payment-icon">
          <Icon name="edit" label={text(`${editText} ${payment.name}`)} cssStyles={editIconStyling} onClick={(e) => editClickHandler(e, payment)}/>
        </div>
      }

    </div>
  );

};


/*********************************/
/** Digital Wallet Payment      **/
/*********************************/
const DigitalWalletPayment = (props) => {
  const { payment, editClickHandler, titledPanel, clickHandler, isCartPayment } = props;
  const { context } = usePaymentsState();

  let paymentPanelStyling = {};
  if (!titledPanel) {
    paymentPanelStyling = {...paymentPanelStyling, height: '130px', padding: '20px'};
  }
  if (isCartPayment) {
    paymentPanelStyling = {...paymentPanelStyling, backgroundColor: 'rgba(189, 139, 202, .25)', border: '3px solid #BD8BCA'};
  }
  const paymentTypeStyling = {
    position: 'relative',
    paddingLeft: '60px',
    marginBottom: '10px'
  };
  const paymentIconStyling = {
    fontSize: '18pt',
    position: 'absolute',
    top: '-12px',
    left: '0px'
  };
  const defaultStyling = {
    color: '#BD8BCA',
    paddingTop: '8px',
    fontStyle: 'italic'
  };
  const emailStyling = {
    paddingRight: '40px',
    fontWeight: '700',
    maxWidth:'260px',
    overflow:'hidden',
    textOverflow:'ellipsis'
  };
  let editIconStyling = {};
  if (!titledPanel) {
    editIconStyling = {...editIconStyling, cursor: 'pointer', position: 'absolute', right: '45px', top: '20px'};
  }
  const editText = text("text.button.label.edit");
  return (
    <div className={titledPanel ? "card__content payment__content" : "panel panel-default"} onClick={()=>clickHandler(context)} style={paymentPanelStyling}>
      <div className="payment-type" style={paymentTypeStyling}>
        <Icon name={getIconNameForPaymentType(payment.type)} cssStyles={paymentIconStyling}/>
        <span>{getNameForPaymentType(payment.type)}</span>
      </div>
      <div>
        <span style={emailStyling}>{payment.displayedAcct}</span>
      </div>
      {payment.defaultPayment ? <div style={defaultStyling}>{text("form.paymentManagement.default")}</div> : null}
      {editClickHandler &&
        <div className="payment-icon">
          <Icon name="edit" label={text(`${editText} ${getNameForPaymentType(payment.type)}`)} cssStyles={editIconStyling} onClick={(e) => editClickHandler(e, payment)}/>
        </div>
      }
    </div>
  );
};


/*********************************/
/** Payment                     **/
/*********************************/
const Payment = (props) => {
  const { payment, onClick, onEdit, isCrm, isCartPayment, payment: { method }, titledPanel } = props;
  const { selectEditPayment } = usePaymentsMutator();
  const { showGlobalModal, hideGlobalModal } = useModalsMutator();

  const clickHandler = (context) => {
    if (onClick) {
      onClick(payment.id);

      let isSubscriptionEnabled = $('#isSubscriptionEnabled').val() === 'true';
      if(isSubscriptionEnabled) {
        let titleText = localText["text.lrp.titleSavedThisOrder"];
        let messageText = localText['text.lrp.savedThisOrder.orderreview'];
        if(context && (context === 'subscriptionSettings' || context === 'crm-subscription')) {
          messageText = localText['text.lrp.savedThisOrder'];
        }
        let closeText = localText["popup.close"];

        const modalName = 'select-payment-success' + payment.id;
        showGlobalModal(modalName, <SuccessModal text={messageText} title={titleText} closeText={closeText} close={() => hideGlobalModal(modalName)} autoClose={5000}/>);
      }
    }
  };

  const editClickHandler = (e, paymentToEdit) => {
    if (onEdit) {
      e.stopPropagation();
      selectEditPayment(paymentToEdit.id);
      onEdit(paymentToEdit, isCrm);
    }
  };

  return (
    <>
      {method === 'card' &&
        <CreditCardPayment payment={payment} editClickHandler={onEdit ? editClickHandler : null} clickHandler={clickHandler} isCartPayment={isCartPayment} titledPanel={titledPanel}/>
      }
      {method !== 'card' &&
        <DigitalWalletPayment payment={payment} editClickHandler={(onEdit && !payment.defaultPayment) ? editClickHandler : null} clickHandler={clickHandler} isCartPayment={isCartPayment} titledPanel={titledPanel}/>
      }
    </>
  );
};


/*********************************/
/** Payment List                **/
/*********************************/
export const PaymentList = (props) => {
  const { clickHandler, type, title, isCrm } = props; // either or both of these can be undefined
  const { payments, cartPaymentId } = usePaymentsState();
  const { viewOptions } = usePaymentViewState();
  const { showEditPayment } = usePaymentViewMutator();
  const { editPayment} = usePaymentsMutator();
  const { registerForm } = useFormObserver(); // all these values could be undefined
  const hasTitle = title && title.length > 0;
  const methods = type === 'cards' ? ['card'] : type === 'digital-wallet' ? ['digital-wallet','paypal', 'apple-pay', 'google-pay'] : [];
  const typedPayments = payments.filter(payment => methods.length === 0 || methods.includes(payment.method));
  if (typedPayments.length === 0) {
    return null;
  }

  const handleClick = (id) => {
    if (clickHandler && viewOptions.selectable) {
      clickHandler(id);
    }
  };

  const handleSubmit = (e) => {
    let defaultPayment = payments.find(payment => payment.defaultPayment);
    if (defaultPayment === undefined){
      defaultPayment = payments[0];
    }
    let payload = {
      cvv: '',
      id: defaultPayment.id,
      nameOnCard: defaultPayment.holderName ,
      expMonth:defaultPayment.expiryMonth ,
      expYear: defaultPayment.expiryYear ,
      billingLine1:defaultPayment.billingAddress.line1,
      billingLine2:defaultPayment.billingAddress.line2,
      billingCity: defaultPayment.billingAddress.town ,
      billingState:defaultPayment.billingAddress.region.isocodeShort ,
      billingZip:  defaultPayment.billingAddress.postalCode,
      market:defaultPayment.billingAddress.region.countryIso,
      makeDefault:true,
      code: defaultPayment.code
    };
    editPayment(payload);
  };

  if (registerForm) {
    useEffect(() => {
      registerForm(handleSubmit);
    }, []);
  }

  const paymentList = typedPayments.map((payment, index) => {
    let isEditable = !isCrm || payment.method === 'card';
    return (
      <div key={index} className={`col-xs-12${viewOptions.fullWidth ? '' : ' col-md-6'}`}>
        <Payment payment={payment} onClick={handleClick} onEdit={isEditable ? showEditPayment : null} isCrm={isCrm} isCartPayment={payment.id === cartPaymentId} selectable={viewOptions.selectable}/>
      </div>
    );
  });

  return (
    <div className="row">
      {hasTitle &&
        <div className='col-xs-12'>
          <h3>{title}</h3>
        </div>
      }
      {paymentList}
    </div>
  );
};


/******************************************/
/** New Payment (used by AddPaymentList) **/
/******************************************/
const NewPayment = (props) => {
  const { icon, method, onClick, name } = props;
  const { context } = usePaymentsState();

  const newPaymentStyling = {
    padding: '11px',
    marginTop: '3px',
    paddingLeft: '30px'
  };
  const newGooglePaymentStyling = {
    padding: '11px',
    marginTop: '3px',
    paddingLeft: '25px'
  };
  const paymentWordsStyling = {
    fontWeight: '700',
    marginTop: '10px',
    padding: '11px',
    paddingLeft: '11px'
  };
  const continueStyling = {
    padding: '11px',
    marginTop:'7px'
  };

  let paymentVisible = true;
  if (method==='apple-pay' && !applePaySupported()) {
    paymentVisible = false;
  } else if (context === 'crm-subscription' && method !== 'card' && method !== 'addCard' && method !== 'crmaddCard') {
    paymentVisible = false;
  }

  return (
    <button id={"pay-method-" + method} className="panel panel-default doterra-payment-item" style={paymentVisible ? {cursor:'pointer',width:'100%'} : {cursor:'pointer', display: 'none'}} onClick={onClick}>
      <div className="row">
        <div className="col-md-2 col-xs-3" style={method !== 'google-pay' ? newPaymentStyling : newGooglePaymentStyling}>
          <Icon name={icon} size='large'/>
        </div>
        <div className="col-md-8 col-xs-7" style={paymentWordsStyling}>
          <div>{name}</div>
        </div>
        <div className="col-md-2 col-xs-2" style={continueStyling}>
          <Icon name="arrows-right" />
        </div>
      </div>
    </button>
  );
};


/*********************************/
/** Add Payment List            **/
/*********************************/
export const AddPaymentList = (props) => {
  const { clickHandler, mode, isCrm } = props;
  const { viewOptions } = usePaymentViewState();
  const { showPaymentList } = usePaymentViewMutator();
  const { methods } = usePaymentsState();
  const list = methods.filter(method => !isCrm || method.method === 'card' || method.method === 'crmaddCard').map(method => {
    return <NewPayment key={method.name} name={method.name} method={method.method} icon={method.icon} onClick={() => clickHandler(method)}/>;
  });

  const backBtnClick = () => {

    if (mode && mode === 'addAPayment') {

      window.location.href = contextPath + '/my-account';
    } else {
      showPaymentList();
    }
  };
  return (
    <div className="row">
      <div className={`col-xs-12${viewOptions.fullWidth ? '' : ' col-md-6'}`}>
        {list}
        {viewOptions.backButton &&
          <Button classes='btn btn-invert btn-size-lg' onClick={backBtnClick}
                  text={text("text.button.label.back")}/>
        }
      </div>
    </div>
  );
};


// TODO: move this to the util/payments
const getCCMessageKey = (status) => {
  let key = null;
  switch (status) {
    case 'EXPIRED':
      key = 'paymentMethod.paymentDetails.expired';
      break;
    case 'EXPIRING':
      key = 'paymentMethod.paymentDetails.expiring';
      break;
    default:
      break;
  }
  return key;
};

/*********************************/
/** Add Express Payment List    **/
/*********************************/
export const AddExpressPaymentList = (props) => {
  const { brainTreeToken, digitalWalletEnv, googlePayMerchantId, mode, modelName, onChange, onSubmit } = props;
  const { viewOptions } = usePaymentViewState();
  const { customerId } = useCustomerState();
  const { showPaymentList } = usePaymentViewMutator();
  const { addPayment, hideModal } = usePaymentsMutator();
  const { methods, cartMarketType } = usePaymentsState();
  const { site } = useSiteState();
  const {formats:{currency:{currencyCode}}} = useMarketState();
  const { showGlobalModal, hideGlobalModal } = useModalsMutator();
  const { showPaymentSelect }= usePaymentViewMutator();

  const determineMarket = (site,cartMarketType) => {
    if (cartMarketType) {
      return cartMarketType;
    }
    return site ? `${site}OTG` : 'USOTG';

  };
  const salesOrg = determineMarket(site,cartMarketType);

  const payload = {
    customerId,
    salesOrg,
    transactionType:"CIT"
  };
  let list = methods.filter(method => {
    return method.method !== 'apple-pay' || applePaySupported();
  }).map(method => {
    return (
      <div key={method.name} className="row panel panel-product panel-default">
        <div className="col-xs-12 col-md-6">
          {text('text.mycart.expressCheckout.helperText', method.name)}
        </div>
        <div className="col-xs-12 col-md-6">
          <div key={method.name} id={method.method} className="express-checkout-btn-container">
            <div className="spinner spinner--brand" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  });

    useMemo(() => {
        requestAnimationFrame(() => {
            post(`/getDHyCSDigitalWalletToken`, JSON.stringify(payload)).then(tokenResultData =>{
                list = methods.filter(method => {
      return method.method !== 'apple-pay' || applePaySupported();
    }).map(method => {
      const handleAlternatePaymentError = (clientErr,text) => {
        const modalName = 'add-digital-payment-error';
        showGlobalModal(modalName,
          <ErrorModal text={text} close={() => hideGlobalModal(modalName)}/>);
        showPaymentSelect();
        console.error('Error creating client:', clientErr);
      };

      client.create({
                      authorization: tokenResultData.clientToken
                    }, (clientErr, clientInstance) => {

        // Stop if there was a problem creating the client.
        // This could happen if there is a network error or if the authorization
        // is invalid.

        if (clientErr) {
          handleAlternatePaymentError(clientErr, text("text.payment.altPayment.error"));
          return;
        }

        const altPaymentParameters = {
          clientInstance,
          addPayment,
          showPaymentList,
          handleAlternatePaymentError,
          modelName,
          hideModal,
          digitalWalletEnv,
          googlePayMerchantId,
          currencyCode
        };

        switch(method.method ? method.method : mode) {
          case "apple-pay":
            initiateApplePay(altPaymentParameters, method.method, null, null);
            break;
          case "paypal":
            initiatePayPal(altPaymentParameters, method.method, null, null);
            break;
          case "google-pay":
            initiateGooglePay(altPaymentParameters, method.method, null, null);
            break;
          default:

        }

      });
    });
  }, (err)=> {
    const handleAlternatePaymentError = (clientErr,text) => {
      const modalName = 'add-digital-payment-error';
      showGlobalModal(modalName,
        <ErrorModal text={text} close={() => hideGlobalModal(modalName)}/>);
      console.error('Error creating client:', clientErr);
                };
                handleAlternatePaymentError(err, text("text.payment.altPayment.error"));
            });
        });
    },[customerId]);

  return (
    <div id="expressCheckoutList">
      {list}
    </div>
  );
};

const handleApplePaySupport = (type) => {
  let lrpErrorTemplate = text('doterra.processnow.errorTemplateMsg');
  let lrpNowMessage = text('doterra.processnow.applePayUnsupported');
  let checkoutMessage = text('doterra.checkout.applePayUnsupported');
  let originalCheckoutMessage = text('doterra.process.creditCheckOutMsg');

  let processNow = $('.process-lrp-now');
  let processNowDiv = processNow.parent('.lrpButtonDiv');
  let checkout = $('.checkout');
  let disabledCheckout = $('.checkoutButtonDiv');

  const disableProcessNow = () => {
    let processNowTitle = processNowDiv.attr('data-original-title');
    if (processNowTitle === "") {
      processNowTitle += lrpErrorTemplate;
      processNowTitle += lrpNowMessage;
      processNow.attr('disabled', true);
    } else {
      processNowTitle += '\n' + lrpNowMessage;
    }
    processNowDiv.attr('data-original-title',processNowTitle);
  };

  const enableProcessNow = () => {
    let processNowTitle = processNowDiv.attr('data-original-title');
    processNowTitle = processNowTitle.replace(new RegExp(lrpNowMessage, 'g'), '');
    let splitValues = processNowTitle.split("- ");
    if(splitValues.length === 1){
      processNow.attr('disabled', false);
      processNowDiv.attr("data-original-title","");
    }
    else{
      processNowDiv.attr("data-original-title",processNowTitle);
    }
  };

  const disableCheckout = () => {
    checkout.hide();
    disabledCheckout.show();
    disabledCheckout.children('.checkOutDisable').attr('data-original-title',checkoutMessage);
  };

  const enableCheckout = () => {
    checkout.show();
    disabledCheckout.hide();
    disabledCheckout.children('.checkOutDisable').attr('data-original-title',originalCheckoutMessage);
  };

  const isProcessNowDisabled = () => {
    return processNow && processNow.attr('disabled') && processNowDiv.attr('data-disabled').includes(lrpNowMessage);
  };

  const isCheckoutDisabled = () => {
    return disabledCheckout && disabledCheckout.is(':visible') && disabledCheckout.children('.checkOutDisable').attr('data-original-title',checkoutMessage);
  };

  // If type is ApplePay and on unsupported browser, disable Process Now
  if (type === "APPLE_PAY" && !applePaySupported()) {
    if (processNow && processNow.length > 0) {
      disableProcessNow();
    } else if ((checkout && checkout.length > 0) || (disabledCheckout && disabledCheckout.length > 0)) {
      disableCheckout();
    }
  } else { // re-enable buttons if they were disabled
        let isContinueDisabled = document.getElementById("isContinueDisabled").value;
        if(!isContinueDisabled) {
        if (isProcessNowDisabled()) {
            enableProcessNow();
        }
        if (isCheckoutDisabled()) {
            enableCheckout();
            }
    }

  }
};

/*********************************/
/** Payment Select (dropdown)   **/
/*********************************/
export const PaymentSelect = (props) => {
  let { showPaymentsModal } = props;
  const { payments, cartPaymentId, getCartPayment } = usePaymentsState();
  const { selectCartPayment } = usePaymentsMutator();

  const items = payments.map(payment => {
    let optionText;
    if (payment.type === 'CASH' || payment.type === 'CHECK') {
      optionText = payment.name ? payment.name : '';
    } else {
      optionText = payment.name ? `${payment.name} ...${payment.number.substring(payment.number.length-4)}` : payment.displayedAcct;
      let iconType = getIconNameForPaymentType(payment.type);
      if (iconType) {
        optionText = `<i class="icon--${iconType} digital-wallet-icon-in-optons"></i> ${optionText}`;
      }
    }
    return <option value={payment.id} key={payment.id} className='paymentOption' data-paymentid={payment.id} data-content={optionText}></option>;
  });


  if (showPaymentsModal) {
    items.unshift(<option value="addNew" key="addNew">{text('text.mycart.dropdown.addNew')}</option>);
  }
  const selected = getCartPayment();
  const ccInfo = selected && selected.method === 'card' ? getCCInfo(selected) : null;
  const hasCCInfo = ccInfo !== null;
  const ccMessageKey = hasCCInfo ? getCCMessageKey(ccInfo.status) : null;

  useEffect(() => {
    if (selected) {
      handleApplePaySupport(selected.type);
    }

    $('.doterra-select').selectpicker('refresh');

  });

  const handleSelected = (value) => {
    if (value === 'addNew') {
      showPaymentsModal();
    } else {
      selectCartPayment(value);
    }
  };

  return (
    <div className="form-group">
      <div className="control-group">
        <div className="controls">
          <select id='payments-payment-select'
                  value={cartPaymentId || ''}
                  className="form-control doterra-select paymentTypesListOptions"
                  onChange={(e) => handleSelected(e.target.value)}
                  aria-label={text('text.mycart.payment.method')}>
            {/* The following line is a gimmick to fix the label; there's potentially some jquery manipulation being invoked */}
            <option value="" disabled/>
            {items}
          </select>
          <label htmlFor="payments-payment-select" className="dropdown-label">{text('text.mycart.payment.method')}</label>
          {ccMessageKey !== null &&
            <div className="text-danger card-info">
              <i className="fa fa-info-circle"/>
              <span>{text(ccMessageKey)}</span>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

export const cardOrDigitalWalletUsedList = (paymentPanelList)=> paymentPanelList.filter(paymentPanel => (paymentPanel.selectedPayment &&
  (paymentPanel.selectedPayment.method === 'digital-wallet' ||
    paymentPanel.selectedPayment.method === 'card')));

const showPayments = (payments, paymentPanelId, paymentPanel) => payments.filter(p => (
  (paymentPanelId === paymentPanel.crmPaymentLineID) ||
  (p.method !== 'digital-wallet' && p.method !== 'card')

));

export const CrmAcceptPaymentTermForm = (props) => {
  const { cardMessage, modelName, closeModal, prevPaymentId, payment, paymentname, showModal } = props;
  const { setAcceptPaymentTerms, selectMultiLinesPayment } = usePaymentsMutator();
  const { acceptPaymentTerms, updatePayment } = usePaymentsState();


  const citMessageStyling = {
    fontSize: '12px',
    marginBottom: '8px'
  };

  const buttonCentering = {
    margin: '20px auto'
  };

  const [copyTerms, setCopyTerms] = useState({
                                               displayText:"Copy payment terms agreement",
                                               copyTerms: "terms agreement"
                                             });

  const inputEl = useRef(null);
  const textAreaEl = useRef(null);

  const onCopyAgreementHandler = () => {
    Tooltip.hide(inputEl.current);
    flushSync(() => {
      setCopyTerms({
                     displayText:'Agreement terms copied',
                     copyTerms: `${cardMessage}`
                   });
    });
    textAreaEl.current.select();
    document.execCommand("copy");

    setTimeout(()=>{
      Tooltip.show(inputEl.current);
    },1);

  };

  const acceptExistPaymentTermsHandler = (e) => {

    if(e.target.checked)
    {setAcceptPaymentTerms(true);}
    else
    {setAcceptPaymentTerms(false);}
  };

  const acceptExistPaymentTermAction = () => {

    post(`${contextPath}/payments/set-payment-used-date/${payment.id}`).then(result => {
      if (result && result.lastUsedDate) {
        payment.lastUsed = result.lastUsedDate;
        updatePayment(payment);
        selectMultiLinesPayment(payment.id, paymentname);
      }else{
        retLrpPaymentLastUsedDateFailed(prevPaymentId);
        const modalName = 'accept-exist-payment-terms-result-error';
        showModal(modalName, <ErrorModal text={text('crm.payment.lrp.cc.update.failed')} close={() => closeModal(modalName)}/>);
      }
    }, error => {
      retLrpPaymentLastUsedDateFailed(prevPaymentId);
      const modalName = 'accept-exist-payment-terms-call-error';
      showModal(modalName, <ErrorModal text={text('crm.payment.lrp.cc.update.failed')} close={() => closeModal(modalName)}/>);
    });

    closeModal(modelName);
  };

  const cancelExistPaymentTermAction = (e) => {

    closeModal(modelName);
    retLrpPaymentLastUsedDateFailed(prevPaymentId);
  };

  return (
    <form id="crmLrpCCLegalForm">
            <textarea data-testid="copiedText"
                      ref={textAreaEl} defaultValue={cardMessage} style={{opacity: 0, position: 'absolute'}}/>
      <div className="row">
        <div className="col-xs-12 col-sm-11">
          <div style={citMessageStyling} dangerouslySetInnerHTML={{ __html: cardMessage}}/>
        </div>
        <div className="col-xs-12 col-sm-1">
                    <span>
                        <i data-testid="copyToClipBoard" ref={inputEl} onClick={onCopyAgreementHandler} className="fa fa-copy" data-tooltip-content={copyTerms.displayText}
                           data-tooltip-id="termsCopy" onMouseLeave={()=> {
                          setCopyTerms({...copyTerms, displayText:"Copy payment terms agreement"});
                          Tooltip.hide();
                        }}
                        />
                        <Tooltip id="termsCopy" variant="light"/>
                    </span>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-sm-12">
          <Checkbox id="acceptExistPaymentTerms1" name="acceptExistPaymentTerms" labelKey={"crm.payment.agree.payment.terms"} classes="form-control"
                    labelClasses="shipping-label" defaultChecked={false} onChange={acceptExistPaymentTermsHandler}/>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-md-6 pull-right">
          <div className="btn-grp pull-right">
            <Button id="lrpPaymentLegalTermsAccepted" disabled={!acceptPaymentTerms}
                    text={text("text.button.label.ok")}
                    classes={"btn btn-primary"} cssStyles={buttonCentering} onClick={acceptExistPaymentTermAction}/>
            <Button id="lrpPaymentLegalTermsCanceled"
                    text={text("text.button.label.cancel")}
                    classes={"btn btn-primary"} cssStyles={buttonCentering} onClick={cancelExistPaymentTermAction}/>
          </div>
        </div>
      </div>
    </form>
  );
};

/*********************************/
/** Payment Select (dropdown)   **/
/*********************************/
export const MultilinesPaymentSelect = (props) => {
  let { nfrFlag, showPaymentsModal, paymentLineID, paymentPK, isLRP, citMitMessage } = props;
  const { payments, selected, crmPaymentSelectedList } = usePaymentsState();
  const { selectMultiLinesPayment, crmAddPayment, setAcceptPaymentTerms, showModal, hideModal } = usePaymentsMutator();

  const cardOrDWUsedList = cardOrDigitalWalletUsedList(crmPaymentSelectedList);
  let showedPayments = payments;
  if (cardOrDWUsedList && cardOrDWUsedList.length > 0) {
    showedPayments = showPayments(payments, paymentLineID, cardOrDWUsedList[0]);
  }

  const items = showedPayments.map(payment => {
    let formattedPaymentSelection;
    if((nfrFlag === 'USNFRAU' || nfrFlag === 'USNFRNZ') && (payment.type === 'CASH' || payment.type === 'CHECK')) {
      return;
    }
    let paymentTypeName = payment.method;
    const paymentSelected = payment.id === paymentPK;
    if(payment.type === 'CASH' || payment.type === 'CHECK'
      || payment.type === 'INSTORE' || payment.type === 'ARBALANCE') {
      formattedPaymentSelection = payment.name ? payment.name : '';
      paymentTypeName = payment.type;
    }
    else if(payment.method === 'digital-wallet' && payment.type !== null) {
      formattedPaymentSelection = `<i class="icon--${getIconNameForPaymentType(payment.type)} digital-wallet-icon-in-optons"></i>  ${payment.displayedAcct}`;
    }
    else {
      formattedPaymentSelection = payment.name ? `${payment.name} ...${payment.number.substring(payment.number.length - 4)}` : '';
    }

    return <option value={payment.method} id={payment.id} key={payment.id} className='paymentOption'
                   data-paymentid={payment.id} selected={paymentSelected} data-content={formattedPaymentSelection}
                   data-paytype={paymentTypeName}>{payment.type}</option>;
  });


  // eslint-disable-next-line no-extra-boolean-cast
  if (!!showPaymentsModal) {
    items.unshift(<option defaultValue="addNew" key="addNew">{text('text.mycart.dropdown.addNew')}</option>);
  }

  let prevPaymentId = 0;
  const currentPaymentPanel = (crmPaymentSelectedList || []).filter(paymentPanel => (paymentPanel.crmPaymentLineID === paymentLineID));
  if (currentPaymentPanel && currentPaymentPanel.length > 0 && currentPaymentPanel[0].selectedPayment)
  {prevPaymentId = currentPaymentPanel[0].selectedPayment.id;}

  useEffect(() => {
    $('.doterra-select').selectpicker('refresh');
  }, [payments, selected, crmPaymentSelectedList, items]);

  const handleMultiLinesSelected = (value, name) => {

    if (value === 'addNew') {
      showPaymentsModal();
    } else if (value) {
      if (isLRP && crmPaymentSelectedList && crmPaymentSelectedList.length < 1) {
        crmAddPayment(value, name);
      }else {

        let noNeedAcceptPaymentTerm = true;
        const payment = (payments || []).find(payment => payment.id === value);
        if (isLRP && citMitMessage && payment && payment.method !== 'digital-wallet') {

          const oneYearTime = 31536000000;
          let lastUsedDate = 0;
          if (payment.lastUsed) {
            lastUsedDate = new Date(payment.lastUsed).getTime();
          }

          if ((new Date().getTime() - lastUsedDate) > oneYearTime) {
            noNeedAcceptPaymentTerm = false;
            const crmAcceptTermModalName = "crm-accept-term-modal";
            setAcceptPaymentTerms(false);
            showModal(crmAcceptTermModalName,
              <Modal title={text('crm.payment.lrp.cc.agreement')} size='large'
                     close={() => hideModal(crmAcceptTermModalName)}>
                <CrmAcceptPaymentTermForm cardMessage={citMitMessage} modelName={crmAcceptTermModalName}
                                          closeModal={hideModal} showModal={showModal} prevPaymentId={prevPaymentId} payment={payment} paymentName={name}/>
              </Modal>);
          }
        }

                if (noNeedAcceptPaymentTerm) {
                  selectMultiLinesPayment(value, name);
                }
      }
    }
  };

  return (
    <div className="form-group">
      <div className="control-group">
        <div className="controls">
          <select id='payments-payment-select' name={paymentLineID} data-size="6" className="form-control doterra-select paymentTypesListOptions"
                  onChange={(e) => handleMultiLinesSelected(e.target.selectedOptions.length > 0 ? e.target.selectedOptions[0].id : 0, e.target.name)}>
            {!paymentPK && !isLRP && <option value=''></option>}
            {items}
          </select>

          <label htmlFor="payments-payment-select" className="dropdown-label">{text('crm.order.payment.types.label')}</label>
        </div>
      </div>
    </div>
  );
};

/*********************************/
/** Credit card type            **/
/*********************************/
// ccType is the one that comes from hybris
export const CreditCardType = (props) => {
  const {ccType, supportedCardTypes} = props;
  const cardTypeStyling = {opacity: '0.5'};
    if(ccType !==null && ccType !== undefined && ccType !==''){
    return (
      <>
        <Icon name="payment-visa" cssStyles={ccType && ccType !== 'visa' ? cardTypeStyling : {}} />
        <Icon name="payment-mastercard" cssStyles={ccType && ccType !== 'master'? cardTypeStyling:{}}/>
        <Icon name="payment-amex" cssStyles={ccType && ccType !== 'amex'? cardTypeStyling:{}}/>
        <Icon name="payment-discover" cssStyles={ccType && ccType !== 'discover'? cardTypeStyling:{}}/>
      </>
    );
  }
  return supportedCardTypes.map((ccType, index) =>{
    let keyType = ccType.key === 'master'? 'mastercard': ccType.key;
    let ccTypeValue = "payment-" + keyType;
    return (
      <React.Fragment key={index}>
        <Icon name={ccTypeValue}/>
      </React.Fragment>
    );
  });
};

/*********************************/
/** McAfee Logo                 **/
/*********************************/
// TODO: if this is used outside of payments as well, we should move it
export const McAfeeLogo = () => {
  const mcAfeeStyling = {
    textAlign:"center",
    border:'0'
  };
  const mcAfeeContextMenu = () => {
    window.open('https://www.mcafeesecure.com/verify?host=doterra.com');
    return false;
  };

  return (
    <div style={mcAfeeStyling}>
      <a rel="noopener noreferrer" target="_blank" href="https://www.mcafeesecure.com/verify?host=doterra.com"><img
        className="mfes-trustmark" src="//cdn.ywxi.net/meter/doterra.com/102.svg" width="90"
        height="37"
        title="McAfee SECURE sites help keep you safe from identity theft, credit card fraud, spyware, spam, viruses and online scams"
        alt="McAfee SECURE sites help keep you safe from identity theft, credit card fraud, spyware, spam, viruses and online scams"
        onContextMenu={mcAfeeContextMenu}/></a>
    </div>
  );
};


/*********************************/
/** Payment Titled Panel        **/
/*********************************/
export const TitledPaymentPanel = ({markets, paymentInfo, digitalWalletEnv, googlePayMerchantId, recaptchaKey, cartShippingAddress, applePayAuth, isOrderConfirmationPage}) => {

  const { getCartPayment } = usePaymentsState();
  const { customerId } = useCustomerState();
  const { cartMarketType } = usePaymentsState();
  const { showGlobalModal, hideGlobalModal } = useModalsMutator();
  const { site } = useSiteState();
  const { showModal, hideModal } = usePaymentsMutator();
  const {formats:{currency:{currencyCode}}} = useMarketState();

  paymentInfo = getCartPayment();
  const hasPayment = paymentInfo !== undefined;
  const paymentsModalName = 'order-payments-modal';

  useEffect(() => {
    const el = document.getElementById('digital-wallet-callout');
    el.innerText = '';
    el.style = '';
    if(paymentInfo && paymentInfo.method === "digital-wallet") {
      el.style = 'margin-bottom: -20px';

      const iconEl = document.createElement("i");
      iconEl.className = `icon--${getIconNameForPaymentType(paymentInfo.type)} icon--md`;

      const spanEl = document.createElement('span');
      spanEl.innerText = `${text('text.payment.method.with')} ${getNameForPaymentType(paymentInfo.type)}`;

      el.appendChild(iconEl);
      el.appendChild(spanEl);

    }
  });


  if(paymentInfo) {
    $('#submit-order').removeClass('disabled');
    if(isOrderConfirmationPage && isOrderConfirmationPage === 'true'){
      const editPaymentIcon = $('#orderReview-payment-panel .icon--edit');
      editPaymentIcon.hide();
    }
    if (paymentInfo.type === "APPLE_PAY" && applePaySupported() && applePayAuth) {
      const editPaymentIcon = $('#orderReview-payment-panel .icon--edit');
      editPaymentIcon.hide();

      const handleAlternatePaymentError = (clientErr,text) => {
        const modalName = 'add-digital-payment-error';
        showGlobalModal(modalName,
          <ErrorModal text={text} close={() => hideGlobalModal(modalName)}/>);
        console.error('Error creating client:', clientErr);
        editPaymentIcon.show();
      };

      if (!applePaySupported()) {
        handleAlternatePaymentError(null,text("doterra.applePay.browserNotSupported"));
        editPaymentIcon.show();
        return;
      }
      $('#submit-order').hide();
      const determineMarket = (site,cartMarketType) => {
        if (cartMarketType) {
          return cartMarketType;
        }
        return site ? `${site}OTG` : 'USOTG';

      };

      const salesOrg = determineMarket(site,cartMarketType);

      const payload = {
        customerId,
        salesOrg,
        transactionType:"CIT"
      };
      post(`/getDHyCSDigitalWalletToken`, JSON.stringify(payload)).then(tokenResultData =>{

        client.create({
                        authorization: tokenResultData.clientToken
                      }, (clientErr, clientInstance) => {

          // Stop if there was a problem creating the client.
          // This could happen if there is a network error or if the authorization
          // is invalid.
          if (clientErr) {
            handleAlternatePaymentError(clientErr, text("text.payment.altPayment.error"));
            editPaymentIcon.show();
            return;
          }
          applePay.create({
                            client: clientInstance
                          }, (applePayErr, applePayInstance) => {
            $('#apple-pay-process-button').append("<div class='spinner spinner--brand spinner--sm' role='status'><span class='sr-only'>Loading...</span></div>");

            if (applePayErr) {
              console.log('Error creating applePayInstance:', applePayErr);
              editPaymentIcon.show();
              return;
            }
            const promise = ApplePaySession.canMakePaymentsWithActiveCard(applePayInstance.merchantIdentifier);
            promise.then(canMakePaymentsWithActiveCard => {
              if (canMakePaymentsWithActiveCard) {
                // Set up Apple Pay buttons
                const applePayButton = document.createElement("button");
                applePayButton.id = "buy-with-apple-pay-doterra";
                applePayButton.className = "apple-pay-button apple-pay-button-black-with-line";
                applePayButton.style = "-apple-pay-button-type: buy;";
                applePayButton.onclick = handleApplePayButtonClick;
                window.applePayInfoMap = {applePayInstance:applePayInstance, handleAlternatePaymentError:handleAlternatePaymentError, editPaymentIcon:editPaymentIcon };
                const applePayProcessDiv = document.getElementById("apple-pay-process-button");
                applePayProcessDiv.textContent = '';
                applePayProcessDiv.appendChild(applePayButton);
                editPaymentIcon.show();
              } else {
                handleAlternatePaymentError(canMakePaymentsWithActiveCard, text("doterra.applePay.noApplePaySetup"));
                console.log("Error with canMakePaymentsWithActiveCard");
                editPaymentIcon.show();
              }
            }).catch((err)=>{
              console.log("ERROR OCCURED: ",err);
              editPaymentIcon.show();
            });

          });

        });
      }, (err)=> {
        handleAlternatePaymentError(err, text("text.payment.altPayment.error"));
      });
    }
    else {
      $('#submit-order').show();
      document.getElementById("apple-pay-process-button").textContent = '';
    }
  }

  function handleApplePayButtonClick() {
    let paymentRequest = window.applePayInfoMap.applePayInstance.createPaymentRequest({
                                                                                        "currencyCode": currencyCode,
                                                                                        "merchantCapabilities": [
                                                                                          "supports3DS"
                                                                                        ],
                                                                                        "supportedNetworks": [
                                                                                          "visa",
                                                                                          "masterCard",
                                                                                          "amex",
                                                                                          "discover"
                                                                                        ],
                                                                                        "total": {
                                                                                          "label": text("doterra.applePay.amountCharged"),
                                                                                          "type": "final",
                                                                                          "amount": document.getElementById("price-for-applepay").value
                                                                                        },
                                                                                        requiredShippingContactFields: ['postalAddress', 'phone', 'email', 'name'],
                                                                                        requiredBillingAddressFields: ['phone', 'email', 'name', 'postalAddress'],
                                                                                        requiredBillingContactFields: ['postalAddress', 'phone', 'email', 'name']

                                                                                      });
    let session = new ApplePaySession(6, paymentRequest);

    session.onvalidatemerchant = event => {
      window.applePayInfoMap.applePayInstance.performValidation({
                                                                  validationURL: event.validationURL,
                                                                  displayName: 'Doterra'
                                                                }, function (err, merchantSession) {
        if(err) {
          // You should show an error to the user, e.g. 'Apple Pay failed to load.'
          console.log("Apple Pay failed to load");
          return;
        }
        session.completeMerchantValidation(merchantSession);
      });
    };

    session.onpaymentauthorized = (event) => {

      window.applePayInfoMap.applePayInstance.tokenize({
                                                         token: event.payment.token
                                                       }, (tokenizeErr, payload) => {
        if(tokenizeErr) {
          console.error('Error tokenizing Apple Pay:', tokenizeErr);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          window.applePayInfoMap.editPaymentIcon.show();
          return;
        }
        const applePayType = 'APPLE_PAY';
        const billingContactEvent = event.payment.billingContact;
        const region = convertRegionToIsocode(billingContactEvent.countryCode, billingContactEvent.administrativeArea);
        if(!region) {
          window.applePayInfoMap.handleAlternatePaymentError(null, text("doterra.applePay.invalidRegion"));
          console.error('Error with region', region);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          window.applePayInfoMap.editPaymentIcon.show();
          return;
        }
        const nameOnCard = payload.details.cardholderName ?
          payload.details.cardholderName :
          `${billingContactEvent.givenName ? billingContactEvent.givenName : ''}  ${billingContactEvent.familyName ? billingContactEvent.familyName : ''}`
            .trim();
        const digitalWalletPayload = {
          token: payload.nonce,
          type: applePayType,
          displayedAcct: payload.details.paymentInstrumentName,
          holderName: nameOnCard,
          email: event.payment.shippingContact.emailAddress,
          defaultPayment: document.getElementById("makeDefault") ? document.getElementById("makeDefault").checked : false,
          billingAddress: {
            line1: billingContactEvent.addressLines[0],
            line2: billingContactEvent.addressLines[1],
            town: billingContactEvent.locality,
            region: {
              isocodeShort: region,
              isocode: `${billingContactEvent.countryCode.toUpperCase()}-${region}`,
              countryIso: billingContactEvent.countryCode.toUpperCase()
            },
            postalCode: billingContactEvent.postalCode,
            country: {
              isocode: billingContactEvent.countryCode.toUpperCase()
            }
          }
        };

        // Send payload.nonce to your server.
        // After you have transacted with the payload.nonce,
        // call `completePayment` to dismiss the Apple Pay sheet.
        session.completePayment(ApplePaySession.STATUS_SUCCESS);

        $("#submit-order").trigger("click", [digitalWalletPayload]);

      });
    };
    session.begin();
  }


  const showPaymentsModal = (mode = 'edit') => {
    showModal(paymentsModalName, <Modal ariaMessage={text('account.overview.paymentMethods')} title={text('account.overview.paymentMethods')} size='medium' close={() => hideModal(paymentsModalName)}>
      <PaymentView markets={markets} initialMode={mode} closeModal={hideModal} bottomBackButton={true} modelName={paymentsModalName} digitalWalletEnv={digitalWalletEnv} googlePayMerchantId={googlePayMerchantId} recaptchaKey={recaptchaKey} cartShippingAddress={cartShippingAddress}/>
    </Modal>);
  };

  const editClickHandler = () => showPaymentsModal('list');

  const editText = text("text.button.label.edit");

  return (
    <TitledPanel icon='click' onEditClick={editClickHandler} text={text("checkout.summary.paymentMethod.header")} label={`${editText}`}>
      {hasPayment && <Payment payment={paymentInfo} titledPanel={true} />}
    </TitledPanel>
  );
};


/*********************************/
/** Payment Well                **/
/*********************************/
export const PaymentWell = (props) => {
  const { paymentInfo } = props;
  return (
    <>
      <h4>{text("checkout.summary.paymentMethod.header")}</h4>
      <div className="well">
        {paymentInfo.method === 'digital-wallet' &&
          <>
            <div>{paymentInfo.type}</div>
            <div>{paymentInfo.email}</div>
          </>
        }
        {paymentInfo.method === 'card' &&
          <div>{paymentInfo.type} {paymentInfo.number}</div>
        }
        <div>{paymentInfo.expiryMonth && paymentInfo.expiryYear ? formatCCExpiry(paymentInfo.expiryMonth, paymentInfo.expiryYear): null}</div>
        <div>{paymentInfo.holderName}</div>
        <div>{paymentInfo.billingAddress.line1}</div>
        <div>{paymentInfo.billingAddress.line2}</div>
        <div>{`${paymentInfo.billingAddress.town}, ${paymentInfo.billingAddress.region.isocodeShort} ${paymentInfo.billingAddress.postalCode}`}</div>
      </div>
    </>
  );
};

export const testables = {CreditCardPayment, getCCMessageKey, handleApplePaySupport};
